import { COMPANY } from "@/lib/company";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Users } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import Photo from "@/components/site/Photo";
import useSeo from "@/hooks/useSeo";

const values = [
  { icon: MapPin, title: "Proximity-first", body: "Everything on Orrbbit is built around what's genuinely nearby — real people and Events in your area." },
  { icon: ShieldCheck, title: "Privacy by design", body: "Discover what's around you without exposing your exact live location. You stay in control of your visibility." },
  { icon: Users, title: "Real-world connection", body: "We help nearby moments turn into real connections — not endless feeds or mass broadcasting." },
];

export default function About() {
  useSeo({ title: "About — Orrbbit", description: "Orrbbit helps you discover people and Events happening around you — proximity-first and privacy-first.", path: "/about" });
  return (
    <div data-testid="about-page">
      <section className="bg-lightbg">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-teal">About Orrbbit</span><p className="mt-3 text-sm text-bodygrey">Operated by {COMPANY.name} · ABN {COMPANY.abn} · ACN {COMPANY.acn}</p>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">Real People. Real Moments. Right Nearby.</h1>
            <p className="mt-5 text-lg text-bodygrey">Orrbbit helps you connect with real people and discover Events — all based around your location.</p>
          </Reveal>
        </div>
      </section>
      <section className="container-x section-space">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-soft">
                  <Icon className="text-teal" size={24} />
                  <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm text-bodygrey">{v.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-10 flex flex-wrap gap-3">
          <Link to="/get-started" className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3 font-semibold text-white transition-transform hover:scale-105">Get Started <ArrowRight size={16} /></Link>
          <Link to="/support" className="inline-flex items-center gap-1 font-semibold text-teal hover:underline">Contact us <ArrowRight size={16} /></Link>
        </Reveal>
      </section>

      {/* Our story in the real world */}
      <section className="container-x pb-16 md:pb-24" data-testid="about-story">
        <Reveal className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Technology should lead people back into the real world.</h2>
          <p className="mt-4 text-bodygrey">Orrbbit was built on a simple belief: the best parts of life happen in person — in neighbourhoods, cafés, parks and local venues. Our technology starts on your phone and ends face-to-face.</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Reveal><Photo slug="city-street-walking" ratio="aspect-[3/4]" className="shadow-soft" /></Reveal>
          <Reveal delay={0.06}><Photo slug="shop-talk" ratio="aspect-[3/4]" className="shadow-soft sm:mt-10" /></Reveal>
          <Reveal delay={0.12}><Photo slug="path-walking" ratio="aspect-[3/4]" className="shadow-soft" /></Reveal>
        </div>
      </section>
    </div>
  );
}

